import { useEffect, useState } from 'react'
import { Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SquareIcon } from './ProviderIcons'
import {
  useSquareOptions,
  useUpdateSquareBooking,
  type ConnectedCalendar,
} from '@/api/calendars'

interface Props {
  calendar: ConnectedCalendar | null
  onClose: () => void
  companyId: number | undefined
}

const TOUS = '__tous__'
const TOUS_SERVICES = '__tous_services__'

/**
 * Ce que l'agent réserve dans Square : un emplacement, un service et,
 * au choix, un membre de l'équipe. Les listes viennent du compte Square.
 */
export function SquareSettingsDialog({ calendar, onClose, companyId }: Props) {
  const { t } = useTranslation()
  const [location, setLocation] = useState('')
  const [member, setMember] = useState(TOUS)
  const [service, setService] = useState('')
  const options = useSquareOptions(companyId, calendar?.id ?? null, location || undefined)
  const update = useUpdateSquareBooking(companyId)

  useEffect(() => {
    if (!calendar?.square) return
    setLocation(calendar.square.location_id)
    setMember(calendar.square.team_member_id || TOUS)
    setService(calendar.square.service_variation_id || TOUS_SERVICES)
  }, [calendar])

  const enregistrer = async () => {
    if (!calendar || !service) return
    const fixe = service === TOUS_SERVICES ? '' : service
    try {
      await update.mutateAsync({
        calendarId: calendar.id,
        location_id: location,
        team_member_id: member === TOUS ? '' : member,
        service_variation_id: fixe,
        is_active: true,
      })
      toast.success(t('manager.calendars.square.saved'))
      onClose()
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: string } } })?.response?.data?.error
      toast.error(t('manager.calendars.square.saveError'), { description: msg })
    }
  }

  const services = options.data?.services ?? []
  const servicesDuMembre =
    member === TOUS
      ? services
      : services.filter((s) => s.team_member_ids.length === 0 || s.team_member_ids.includes(member))

  return (
    <Dialog open={!!calendar} onOpenChange={(o) => !o && onClose()}>
      <DialogContent showCloseButton={false} className="sm:max-w-md overflow-hidden border-0 p-0 shadow-2xl">
        <div className="h-1 w-full bg-gradient-to-r from-blue-600 via-blue-400 to-blue-600" />
        <div className="p-6 pb-0">
          <div className="flex items-start gap-3">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-white dark:bg-slate-800 shadow-sm ring-1 ring-black/5 dark:ring-white/10">
              <SquareIcon className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-xl tracking-tight">
                {t('manager.calendars.square.title')}
              </DialogTitle>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {t('manager.calendars.square.subtitle')}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4 px-6 pt-4 pb-2">
          {options.isLoading ? (
            <div className="py-6 text-center">
              <Loader2 className="h-5 w-5 animate-spin text-slate-400 mx-auto" />
            </div>
          ) : options.isError ? (
            <p className="text-sm text-red-600">{t('manager.calendars.square.loadError')}</p>
          ) : (
            <>
              {(options.data?.locations.length ?? 0) > 1 && (
                <div>
                  <Label className="text-sm font-medium">{t('manager.calendars.square.location')}</Label>
                  <Select value={location} onValueChange={(v) => { setLocation(v); setMember(TOUS) }}>
                    <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {options.data?.locations.map((l) => (
                        <SelectItem key={l.id} value={l.id}>{l.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}

              <div>
                <Label className="text-sm font-medium">{t('manager.calendars.square.service')}</Label>
                <Select value={service} onValueChange={setService}>
                  <SelectTrigger className="mt-1.5">
                    <SelectValue placeholder={t('manager.calendars.square.pickService')} />
                  </SelectTrigger>
                  <SelectContent>
                    {services.length > 1 && (
                      <SelectItem value={TOUS_SERVICES}>{t('manager.calendars.square.allServices', { count: services.length })}</SelectItem>
                    )}
                    {servicesDuMembre.map((s) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name} · {s.duration_minutes} min
                        {s.price != null ? ` · ${s.price} ${s.currency ?? ''}` : ''}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500 mt-1.5">
                  {services.length === 0
                    ? t('manager.calendars.square.noService')
                    : service === TOUS_SERVICES
                      ? t('manager.calendars.square.allServicesHelp')
                      : t('manager.calendars.square.oneServiceHelp')}
                </p>
              </div>

              <div>
                <Label className="text-sm font-medium">{t('manager.calendars.square.member')}</Label>
                <Select value={member} onValueChange={setMember}>
                  <SelectTrigger className="mt-1.5"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value={TOUS}>{t('manager.calendars.square.anyMember')}</SelectItem>
                    {options.data?.team_members.map((m) => (
                      <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t bg-slate-50 dark:bg-slate-800/50 px-6 py-4 mt-4">
          <Button type="button" variant="ghost" onClick={onClose} disabled={update.isPending}>
            {t('manager.calendars.modal.cancel')}
          </Button>
          <Button
            type="button"
            onClick={() => void enregistrer()}
            disabled={update.isPending || !service || options.isLoading}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          >
            {update.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {t('manager.calendars.square.save')}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  )
}
